import React, { useState, useEffect, useMemo } from 'react'; // Added useEffect, useMemo
import VeiculoList from '../components/VeiculoList';
import VeiculoForm from '../components/VeiculoForm';
import api from '../services/api'; // Added api import
import { Container, Typography, Box, Button, IconButton, Collapse, TextField, Autocomplete, CircularProgress } from '@mui/material'; // Added IconButton, Collapse, TextField, Autocomplete, CircularProgress
import AddIcon from '@mui/icons-material/Add';
import FilterListIcon from '@mui/icons-material/FilterList'; // New import
import { debounce } from 'lodash'; // Added debounce
import { actionButtonSx, filterCardSx, pageTitleSx } from '../styles/uiTokens';

function VeiculosPage() {
  const [refreshList, setRefreshList] = useState(false);
  const [editingVeiculo, setEditingVeiculo] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [showFilters, setShowFilters] = useState(false); // State for filter visibility

  // Filter states
  const [placaFilter, setPlacaFilter] = useState('');
  const [selectedProprietarioIdFilter, setSelectedProprietarioIdFilter] = useState('');

  // Autocomplete states for Proprietario filter
  const [proprietarioOptions, setProprietarioOptions] = useState([]);
  const [loadingProprietarios, setLoadingProprietarios] = useState(false);
  const [selectedProprietarioFilter, setSelectedProprietarioFilter] = useState(null); // Holds the selected Pessoa object

  // Debounced fetch for Pessoas (Proprietarios)
  const debouncedFetchPessoas = useMemo(() => debounce((searchTerm) => {
      setLoadingProprietarios(true);
      api.get(`/pessoas?search=${searchTerm}`)
        .then(response => setProprietarioOptions(response.data.data || response.data))
        .catch(error => console.error('Erro ao buscar pessoas para filtro:', error))
        .finally(() => setLoadingProprietarios(false));
    }, 500),
    []
  );
  useEffect(() => {
    return () => {
      debouncedFetchPessoas.cancel();
    };
  }, [debouncedFetchPessoas]);

  // Effect to trigger list refresh when filters change
  useEffect(() => {
    setRefreshList(prev => !prev);
  }, [placaFilter, selectedProprietarioIdFilter]);

  const handleVeiculoAdded = () => {
    setRefreshList(prev => !prev);
    setEditingVeiculo(null);
    setShowForm(false);
  };

  const handleEditVeiculo = (veiculo) => {
    setEditingVeiculo(veiculo);
    setShowForm(true);
  };

  const handleCancelEditVeiculo = () => {
    setEditingVeiculo(null);
    setShowForm(false);
  };

  const handleVeiculoDeleted = () => {
    setRefreshList(prev => !prev);
  };

  const handleShowForm = () => {
    setEditingVeiculo(null);
    setShowForm(true);
  };

  return (
    <Container sx={{ mt: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h4" gutterBottom sx={pageTitleSx}>
          Gerenciar Ve&iacute;culos
        </Typography>
        <Box> {/* Group buttons */}
          {!showForm && (
            <>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={handleShowForm}
                sx={{ ...actionButtonSx, mr: 1 }}
              >
                Adicionar Ve&iacute;culo
              </Button>
              <IconButton
                onClick={() => setShowFilters(prev => !prev)}
                aria-label="toggle filters"
                sx={{ color: 'primary.main' }}
              >
                <FilterListIcon />
              </IconButton>
            </>
          )}
        </Box>
      </Box>

      {!showForm && ( // Only show filters if form is not active
        <Collapse in={showFilters}>
          <Box sx={filterCardSx}>
            <Typography variant="h6" gutterBottom>Filtros</Typography>
            <TextField
              label="Placa"
              fullWidth
              margin="normal"
              value={placaFilter}
              onChange={(e) => setPlacaFilter(e.target.value)}
            />
            <Autocomplete
              id="proprietario-filter-autocomplete"
              options={proprietarioOptions}
              getOptionLabel={(option) => option.nome || ''}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              value={selectedProprietarioFilter}
              onChange={(event, newValue) => {
                setSelectedProprietarioFilter(newValue);
                setSelectedProprietarioIdFilter(newValue ? newValue.id : '');
              }}
              onInputChange={(event, newInputValue, reason) => {
                if (reason === 'input') {
                  if (newInputValue) {
                    debouncedFetchPessoas(newInputValue);
                  } else {
                    setProprietarioOptions([]);
                  }
                }
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Propriet\u00E1rio"
                  variant="outlined"
                  margin="normal"
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {loadingProprietarios ? <CircularProgress color="inherit" size={20} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />
          </Box>
        </Collapse>
      )}

      {showForm && (
        <VeiculoForm
          onVeiculoAdded={handleVeiculoAdded}
          initialData={editingVeiculo}
          onCancelEdit={handleCancelEditVeiculo}
        />
      )}

      {!showForm && (
        <VeiculoList
          refresh={refreshList}
          onEdit={handleEditVeiculo}
          onVeiculoDeleted={handleVeiculoDeleted}
          placaFilter={placaFilter}
          proprietarioIdFilter={selectedProprietarioIdFilter}
        />
      )}
    </Container>
  );
}

export default VeiculosPage;
